import MobileTabsHeader from 'components/TabsHeader/MobileTabsHeader';
import TabsHeader from 'components/TabsHeader/TabsHeader';
import { usePerms } from 'contexts/PermsContext';
import DopplerRadar from 'features/Weather/features/WeatherMaps/features/DopplerRadar/index.web.jsx';
import Hurricanes from 'features/Weather/features/WeatherMaps/features/Hurricanes/index.web.jsx';
import CurrentConditions from 'features/Weather/features/WeatherMaps/features/CurrentConditions/index.web.jsx';
import Earthquakes from 'features/Weather/features/WeatherMaps/features/Earthquakes/index.web.jsx';
import SevereWeatherAlerts from 'features/Weather/features/WeatherMaps/features/SevereWeatherAlerts/index.web.jsx';
import AllBuoysMap from 'features/Integrations/features/ConnectedDevices/features/Buoy/map/index.web';
import USGSFullScreen from 'pages/System/USGSFullScreen/USGSFullScreen';
import { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';

const MAP_ACCESS_LEVELS = [8, 9, 10, 11, 12, 13];

const MAP_TABS = [
  { option: 'Severe Weather Warnings', Component: SevereWeatherAlerts },
  { option: 'Current Conditions', Component: CurrentConditions },
  { option: 'Doppler', Component: DopplerRadar },
  { option: 'Earthquake', Component: Earthquakes },
  { option: 'Hurricane', Component: Hurricanes },
  { option: 'USGS Map', Component: USGSFullScreen, requiredPermission: 'canViewUsgsMap' },
  { option: 'Buoy Map', Component: AllBuoysMap, requiredPermission: 'canViewBuoyMap' },
];

export default function Maps() {
  const [params, setParams] = useSearchParams();
  const perms = usePerms();
  const navigate = useNavigate();
  const [mapPermissions, setMapPermissions] = useState(null);

  useEffect(() => {
    let shouldUsePermissionResult = true;

    const loadMapPermissions = async () => {
      const canViewMaps = await perms.validatePermissions(['MapsEnabled']);

      if (!shouldUsePermissionResult) return;

      if (!canViewMaps) {
        navigate('/404');
        return;
      }

      const canViewUsgsMap = perms.testPermission(MAP_ACCESS_LEVELS, [
        'ViewUSGSMonitoringStationConnections',
      ]);
      const canViewBuoyMap = perms.testPermission(MAP_ACCESS_LEVELS, [
        'WeatherEnabled',
        'MapsEnabled',
        'MarineForecastEnabled',
        'IntegrationsEnabled',
      ]);

      setMapPermissions({ canViewBuoyMap, canViewUsgsMap });
    };

    loadMapPermissions();

    return () => {
      shouldUsePermissionResult = false;
    };
  }, [navigate, perms]);

  const availableTabs = MAP_TABS.filter(
    ({ requiredPermission }) => !requiredPermission || mapPermissions?.[requiredPermission],
  );
  const options = availableTabs.map(({ option }) => ({ option }));
  const requestedTab = params.get('tab');
  const selectedTab =
    availableTabs.find(({ option }) => option.toLowerCase() === requestedTab?.toLowerCase()) ||
    availableTabs[0];
  const SelectedMap = selectedTab?.Component;

  useEffect(() => {
    if (!mapPermissions || !selectedTab) return;
    if (requestedTab === selectedTab.option) return;

    const newParams = new URLSearchParams(params.toString());
    newParams.set('tab', selectedTab.option);
    setParams(newParams, { replace: true });
  }, [mapPermissions, params, requestedTab, selectedTab, setParams]);

  if (!mapPermissions) return null;

  return (
    <Container fluid className="d-flex h-100 min-h-0 w-100 flex-column overflow-hidden px-3 py-3">
      <div className="d-none d-lg-block w-100 flex-shrink-0">
        <TabsHeader options={options} styles={[{ fontSize: 12 }]} />
      </div>
      <div className="d-block d-lg-none w-100 flex-shrink-0">
        <MobileTabsHeader options={options} />
      </div>
      <div className="d-flex min-h-0 w-100 flex-grow-1 overflow-auto">
        {SelectedMap && <SelectedMap />}
      </div>
    </Container>
  );
}
